import { Table } from 'antd'
import { observer } from 'mobx-react-lite'

import { LOGISTICIANS_INVOLVEMENT_METRICS } from '../Contracts/ILogisticiansInvolvementReport'
import { formatReportCount } from '../Helpers/reportFormat'
import { NUMERIC_SORT_DIRECTIONS, sortFromTable, sortOrderOf } from '../Helpers/reportSort'
import { logisticiansInvolvementReportStore as store } from '../Stores/ReportStore'
import { ReportUserCell } from './ReportUserCell'

import type {
    ILogisticiansInvolvementReport,
    ILogisticiansInvolvementRow,
    LogisticiansInvolvementMetric,
} from '../Contracts/ILogisticiansInvolvementReport'
import type { TableColumnsType } from 'antd'

/**
 * Колонки легасі (`ReportLogisticiansInvolvementCrud.jsx:15-24`, З-07): пара
 * «створено / запропоновано» — однією колонкою, як у легасі; сортується вона
 * за першим числом.
 */
const COLUMNS: { metric: LogisticiansInvolvementMetric; title: string; pair?: LogisticiansInvolvementMetric }[] = [
    { metric: 'applicationsCreated', title: 'Створено заявок' },
    { metric: 'applicationOffers', title: 'Пропозиції по заявкам' },
    { metric: 'applicationsConfirmed', title: 'Підтверджені' },
    { metric: 'applicationsUnloaded', title: 'Вивантаження' },
    { metric: 'applicationsLost', title: 'Програно' },
    { metric: 'requestsCreated', title: 'Запитів: Створено/Запропоновано', pair: 'requestOffers' },
    { metric: 'tendersCreated', title: 'Тендерів: Створено/Запропоновано', pair: 'tenderOffers' },
]

/**
 * Таблиця «Звіт по залученню логістів» (`SPEC-027` §4.3). Підсумку немає ні в
 * легасі, ні в беку; сортування — бек (К-06).
 */
export const LogisticiansInvolvementTable = observer(({ report }: { report: ILogisticiansInvolvementReport }) => {
    const columns: TableColumnsType<ILogisticiansInvolvementRow> = [
        {
            key: 'user',
            title: 'Логіст',
            render: (_: unknown, row) => <ReportUserCell user={row.user} />,
        },
        ...COLUMNS.map(({ metric, title, pair }) => ({
            key: metric,
            title,
            align: 'right' as const,
            sorter: true,
            sortDirections: NUMERIC_SORT_DIRECTIONS,
            sortOrder: sortOrderOf(store.sort, metric),
            render: (_: unknown, row: ILogisticiansInvolvementRow) =>
                pair === undefined
                    ? formatReportCount(row[metric])
                    : `${formatReportCount(row[metric])} / ${formatReportCount(row[pair])}`,
        })),
    ]

    return (
        <Table<ILogisticiansInvolvementRow>
            data-testid="report-involvement-table"
            rowKey={(row) => row.user.uuid}
            columns={columns}
            dataSource={report.rows}
            pagination={false}
            loading={store.isLoading}
            scroll={{ x: 'max-content' }}
            showSorterTooltip={false}
            onChange={(_pagination, _filters, sorter) => {
                const single = Array.isArray(sorter) ? sorter[0] : sorter

                void store.setSort(sortFromTable(LOGISTICIANS_INVOLVEMENT_METRICS, single?.columnKey, single?.order))
            }}
        />
    )
})
